<template>
  <div
    v-loading="isCreatingOptions"
    class="style-option-controller"
  >
    <div
      :class="[
        'style-option-controller__header',
        {
          'style-option-controller__header--no-children': options.length === 0
        }
      ]"
    >
      <DropArea
        :title="controllerTitle"
        @handle-files="handleFiles"
      />
      <ElButton
        v-if="options.length > 1 && latestDeliveryAssigned"
        type="text"
        class="style-option-controller__assign-delivery"
        @click="assignDeliveryToAll"
      >
        {{ `Assign ${latestDeliveryAssigned.name} to all options` }}
      </ElButton>
    </div>

    <section class="style-option-controller__options">
      <Draggable
        v-if="options.length > 0"
        ref="optionsContainer"
        :disabled="!isDraggable"
        :value="options"
        ghost-class="option-dragged__placeholder"
        v-bind="{ animation: 100 }"
        @end="onDrop"
      >
        <div
          v-for="(option, optionIndex) in options"
          :key="option.id"
          class="style-option-controller__option-wrapper"
        >
          <StyleOption
            :option-title="`Option ${optionIndex + 1}`"
            :option-id="option.id"
            :style-id="styleId"
            :option-meta-filters="optionMetaFilters"
          />
          <div
            class="style-option-controller__option-delete"
            @click.once="removeOption(option.id)"
          >
            <i class="el-icon-close" />
          </div>
        </div>
      </Draggable>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Draggable from 'vuedraggable'

import StyleOption from '@/components/project/StyleOption.vue'
import DropArea from '@/components/DropArea.vue'

import { DragAndDrop } from '@/mixins/DragAndDrop'

export default {
  name: 'StyleOptionController',

  components: {
    StyleOption,
    Draggable,
    DropArea
  },

  mixins: [DragAndDrop],

  props: {
    controllerTitle: String,
    styleId: {
      required: true,
      type: Number
    }
  },

  data: () => ({
    isCreatingOptions: false,
    isDraggable: true
  }),

  computed: {
    ...mapGetters([
      'getOptionsByStyleId',
      'getLatestDeliveryAssigned',
      'getOptionMetaFilters'
    ]),

    /**
     * @returns {Array}
     */
    options () {
      return this.getOptionsByStyleId(this.styleId)
    },

    /**
     * @returns {object}
     */
    latestDeliveryAssigned () {
      return this.getLatestDeliveryAssigned()
    },

    /**
     * @returns {object}
     */
    optionMetaFilters () {
      return this.getOptionMetaFilters
    }
  },

  created () {
    this.fetchOptionMetaFilters()
  },

  methods: {
    ...mapActions([
      'fetchOptionMetaFilters',
      'createOption',
      'deleteOption',
      'updateOption',
      'setLatestDeliveryAssigned',
      'setOptionUploadStatus'
    ]),

    /**
     * @param   {number} index
     *
     * @returns {object}
     */
    getOptionByListIndex (index) {
      return this.options[index]
    },

    /**
     * @param {CustomEvent} dragEvent
     */
    onDrop (dragEvent) {
      this.isDraggable = false
      this.stopDraggingReorder({
        type: 'option',
        destinationIndex: dragEvent.newDraggableIndex,
        item: this.getOptionByListIndex(dragEvent.oldDraggableIndex),
        list: this.options
      })
      this.isDraggable = true
    },

    /**
     * @param {Array} files
     */
    async handleFiles (files) {
      this.isDraggable = false
      this.setOptionUploadStatus(true)
      this.isCreatingOptions = true

      for (const file of files) {
        await this.createNewOption(file)
      }

      this.setOptionUploadStatus(false)
      this.isCreatingOptions = false
      this.isDraggable = true
    },

    /**
     * @param   {File}   file
     *
     * @returns {object}
     */
    async createNewOption (file) {
      this.isDraggable = false
      let latestIndex = 0

      if (this.options.length > 0) {
        latestIndex = this.options[this.options.length - 1].index
      }

      const option = await this.createOption({
        styleId: this.styleId,
        index: latestIndex + 1,
        file: file,
        meta: {}
      })
      this.isDraggable = true

      return option
    },

    /**
     * @param {number} optionId
     */
    async removeOption (optionId) {
      this.isDraggable = false
      const payload = {
        styleId: this.styleId,
        optionId
      }

      await this.deleteOption(payload)
      this.isDraggable = true
    },

    /**
     */
    assignDeliveryToAll () {
      this.options.forEach(option => {
        this.updateOption({
          styleId: this.styleId,
          optionId: option.id,
          meta: {
            delivery_id: this.latestDeliveryAssigned.id
          }
        })
      })

      this.setLatestDeliveryAssigned(null)
    }
  }
}
</script>

<style lang="scss" scoped>
.style-option-controller {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: $white;
  border: $border-grey-extra-light;
  border-radius: $border-radius-default;
}

.style-option-controller__header {
  border-bottom: $border-grey;

  &.style-option-controller__header--no-children {
    border-bottom: 0;
  }
}

.style-option-controller__assign-delivery {
  width: 100%;
  padding-top: 0;
}

.style-option-controller__option-wrapper {
  position: relative;
  border-bottom: $border-grey-extra-light;

  .style-option-controller__option-delete {
    position: absolute;
    top: 0;
    right: 0;
    padding: $spacer-unit;
    color: $red;
    font-weight: $font-weight-bold;
    font-size: $font-size-large;
    cursor: pointer;
    opacity: 0;
  }

  &:hover {
    .style-option-controller__option-delete {
      opacity: 1;
    }
  }

  &:last-child {
    border-bottom: 0;
  }
}

.style-option-controller__options {
  @include scroll-y-smooth();
}

.option-dragged__placeholder {
  @include element-dragged-placeholder;
}
</style>
